/**
 * YouTube Markers
 *
 * Parse and normalize YouTube markdown markers for chat rendering.
 *
 * Primary responsibilities:
 * - Validate supported YouTube URLs and video IDs
 * - Normalize single-video and video-list marker payloads
 *
 * Dependencies:
 * - None
 */

export const YOUTUBE_VIDEO_MARKER_REGEX =
  /\\?\[YOUTUBE_VIDEO\\?\]([\s\S]*?)\\?\[\/YOUTUBE_VIDEO\\?\]/g
export const YOUTUBE_VIDEO_LIST_MARKER_REGEX =
  /\\?\[YOUTUBE_VIDEO_LIST\\?\]([\s\S]*?)\\?\[\/YOUTUBE_VIDEO_LIST\\?\]/g

const YOUTUBE_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/
const MAX_YOUTUBE_LIST_ITEMS = 8

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com'
])

const YOUTUBE_THUMBNAIL_HOSTS = new Set(['i.ytimg.com', 'img.youtube.com'])

export type YoutubeVideoMarkerInput = {
  url: string
  title?: string
  channel?: string
  duration?: string
  summary?: string
  reason?: string
  thumbnailUrl?: string
}

export type YoutubeVideoMarker = YoutubeVideoMarkerInput & {
  id: string
  embedUrl: string
  thumbnailUrl: string
}

export type YoutubeVideoListMarker = {
  title?: string
  videos: YoutubeVideoMarker[]
}

// --------------

const asString = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

const stripUrlNoise = (value: string) => value.replace(/^<|>$/g, '').replace(/^"|"$/g, '').trim()

const normalizeVideoId = (value: string) => {
  const candidate = value.trim()

  return YOUTUBE_ID_REGEX.test(candidate) ? candidate : null
}

const parseYoutubeUrl = (value: string) => {
  try {
    return new URL(value)
  } catch {
    return null
  }
}

const getVideoIdFromUrl = (url: URL) => {
  const host = url.hostname.toLowerCase()

  if (host === 'youtu.be') {
    return normalizeVideoId(url.pathname.split('/').filter(Boolean)[0] ?? '')
  }

  if (!YOUTUBE_HOSTS.has(host)) return null

  if (url.pathname === '/watch') {
    return normalizeVideoId(url.searchParams.get('v') ?? '')
  }

  const [, route, id] = url.pathname.split('/')
  if (['embed', 'shorts', 'live'].includes(route ?? '')) {
    return normalizeVideoId(id ?? '')
  }

  return null
}

const safeJsonParse = (value: string): unknown => {
  const trimmed = value.trim()

  try {
    return JSON.parse(trimmed)
  } catch {
    try {
      return JSON.parse(trimmed.replace(/\\"/g, '"'))
    } catch {
      return null
    }
  }
}

const getVideoUrl = (payload: Record<string, unknown>) =>
  asString(payload.url) ||
  asString(payload.videoUrl) ||
  asString(payload.href) ||
  asString(payload.link)

const getPayloadVideoId = (payload: Record<string, unknown>) =>
  asString(payload.videoId) || asString(payload.id)

const getSafeThumbnailUrl = (value: string, videoId: string) => {
  const fallback = getYoutubeThumbnailUrl(videoId)
  if (!value) return fallback

  const url = parseYoutubeUrl(value)
  if (!url || url.protocol !== 'https:') return fallback
  if (!YOUTUBE_THUMBNAIL_HOSTS.has(url.hostname.toLowerCase())) return fallback
  if (!url.pathname.split('/').includes(videoId)) return fallback

  return url.toString()
}

const normalizeYoutubeVideo = (value: unknown): YoutubeVideoMarker | null => {
  if (typeof value === 'string') {
    const url = stripUrlNoise(value)
    const id = extractYoutubeVideoId(url)
    if (!id) return null

    return {
      id,
      url,
      title: 'YouTube video',
      embedUrl: getYoutubeEmbedUrl(id),
      thumbnailUrl: getYoutubeThumbnailUrl(id)
    }
  }

  if (!value || typeof value !== 'object') return null

  const payload = value as Record<string, unknown>
  const url = stripUrlNoise(getVideoUrl(payload))
  const id = extractYoutubeVideoId(url || getPayloadVideoId(payload))
  if (!id) return null

  const title = asString(payload.title) || 'YouTube video'
  const channel = asString(payload.channel) || undefined
  const duration = asString(payload.duration) || undefined
  const summary =
    asString(payload.summary) ||
    asString(payload.reason) ||
    asString(payload.description) ||
    undefined
  const thumbnailUrl = getSafeThumbnailUrl(asString(payload.thumbnailUrl), id)

  return {
    id,
    url: url || `https://www.youtube.com/watch?v=${id}`,
    title,
    channel,
    duration,
    summary,
    thumbnailUrl,
    embedUrl: getYoutubeEmbedUrl(id)
  }
}

// --------------

export const extractYoutubeVideoId = (value: string) => {
  const input = stripUrlNoise(value)
  if (!input) return null

  if (YOUTUBE_ID_REGEX.test(input)) return input

  const url = parseYoutubeUrl(input)
  if (!url) return null

  return getVideoIdFromUrl(url)
}

export const getYoutubeEmbedUrl = (id: string) => `https://www.youtube-nocookie.com/embed/${id}`

export const getYoutubeThumbnailUrl = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`

export const parseYoutubeVideoMarkerPayload = (value: string) => {
  const parsed = safeJsonParse(value)

  return normalizeYoutubeVideo(parsed ?? value)
}

export const parseYoutubeVideoListMarkerPayload = (
  value: string
): YoutubeVideoListMarker | null => {
  const parsed = safeJsonParse(value)

  if (Array.isArray(parsed)) {
    const videos = parsed
      .map(normalizeYoutubeVideo)
      .filter((video): video is YoutubeVideoMarker => Boolean(video))
      .slice(0, MAX_YOUTUBE_LIST_ITEMS)

    return videos.length > 0 ? { videos } : null
  }

  if (!parsed || typeof parsed !== 'object') return null

  const payload = parsed as Record<string, unknown>
  const rawVideos = Array.isArray(payload.videos) ? payload.videos : []
  const videos = rawVideos
    .map(normalizeYoutubeVideo)
    .filter((video): video is YoutubeVideoMarker => Boolean(video))
    .slice(0, MAX_YOUTUBE_LIST_ITEMS)

  if (videos.length === 0) return null

  const title = asString(payload.title) || undefined

  return { title, videos }
}

export const serializeYoutubeVideoMarkerTag = (video: YoutubeVideoMarkerInput) =>
  `[YOUTUBE_VIDEO]${JSON.stringify(video)}[/YOUTUBE_VIDEO]`

export const serializeYoutubeVideoListMarkerTag = (marker: {
  title?: string
  videos: YoutubeVideoMarkerInput[]
}) => `[YOUTUBE_VIDEO_LIST]${JSON.stringify(marker)}[/YOUTUBE_VIDEO_LIST]`
